import { useCartStore } from "../../store/cartStore";
import { XIcon, TrashIcon } from "@heroicons/react/outline";
import { CURRENCY_MAP } from "../../constants";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../store/userStore";
import { usePixel } from "../../hooks/usePixel";

const getRemaining = (ticket) => {
  if (ticket.quantity_total != null && ticket.quantity_sold != null) {
    return Math.max(0, ticket.quantity_total - ticket.quantity_sold);
  }
  return 20;
};

export default function CartDrawer() {
  const {
    isDrawerOpen,
    closeDrawer,
    items,
    removeItem,
    updateQuantity,
    getSubtotal,
    getCommission,
    getVat,
    getTotal,
  } = useCartStore();

  const navigate = useNavigate();

  const subtotal = getSubtotal();
  const commission = getCommission();
  const total = getTotal();
  const vat = getVat();

  const hasCommission = commission > 0;
  const hasVat = vat > 0;

  const { isAuthenticated } = useUserStore();

  const { trackEvent } = usePixel(items[0]?.pixels || []);

  const formatCurrency = (amount, currency) => {
    const symbol = CURRENCY_MAP[currency] || currency;
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const handleCheckout = () => {
    closeDrawer();

    trackEvent("InitiateCheckout", {
      value: total,
      currency: items[0]?.currency,
      contents: items.map((i) => ({
        id: i.ticket.id,
        quantity: i.quantity,
      })),
    });

    const requiresLogin = items.some((i) => i.can_buy_without_login === false);
    if (requiresLogin && !isAuthenticated) {
      navigate("/tickets-store/auth?redirect=checkout");
    } else {
      navigate("/tickets-store/checkout");
    }
  };

  return (
    <>
      {/* BACKDROP */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30"
          onClick={closeDrawer}
        />
      )}

      {/* DRAWER */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-sm bg-[#02160e] border-l border-green-900/40 z-40 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-green-900/30">
          <div>
            <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
              Tu selección
            </p>
            <h2 className="text-lg font-semibold">Carrito</h2>
          </div>
          <button
            onClick={closeDrawer}
            className="text-gray-400 hover:text-white transition"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ITEMS */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 gap-3">
              <span className="text-4xl">🎟</span>
              <p className="text-sm">Tu carrito está vacío</p>
              <p className="text-xs text-gray-600">
                Añade entradas para continuar
              </p>
            </div>
          ) : (
            items.map((item) => (
              <CartItem
                key={`${item.eventId}-${item.ticket.id}`}
                item={item}
                onRemove={() => removeItem(item.ticket.id, item.eventId)}
                onQtyChange={(q) =>
                  updateQuantity(item.ticket.id, item.eventId, q)
                }
                formatCurrency={formatCurrency}
              />
            ))
          )}
        </div>

        {/* FOOTER */}
        {items.length > 0 && (
          <div className="px-5 py-5 border-t border-green-900/30 space-y-3">
            {/* SUBTOTAL */}
            <div className="flex items-center justify-between">
              <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                Subtotal
              </p>
              <p className="text-sm text-white">
                {formatCurrency(subtotal, items[0]?.currency)}
              </p>
            </div>

            {hasCommission && (
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                  Comisión
                </p>
                <p className="text-sm text-gray-300">
                  {formatCurrency(commission, items[0]?.currency)}
                </p>
              </div>
            )}

            {hasVat && (
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                  IVA
                </p>
                <p className="text-sm text-gray-300">
                  {formatCurrency(vat, items[0]?.currency)}
                </p>
              </div>
            )}

            {/* DIVIDER */}
            <div className="border-t border-green-900/30 pt-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-widest text-gray-400 uppercase">
                  Total
                </p>
                <p className="text-xl font-bold text-green-400">
                  {formatCurrency(total, items[0]?.currency)}
                </p>
              </div>
            </div>

            {/* CHECKOUT BTN */}
            <button
              onClick={handleCheckout}
              className="w-full py-3 rounded-xl bg-green-500 text-black font-semibold tracking-widest uppercase text-sm hover:bg-green-400 transition"
            >
              Confirmar pedido →
            </button>

            <button
              onClick={closeDrawer}
              className="w-full py-2 text-xs text-green-400/60 hover:text-green-400 tracking-widest uppercase transition"
            >
              Seguir eligiendo
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/* ── CART ITEM ── */

function CartItem({ item, onRemove, onQtyChange, formatCurrency }) {
  const remaining = getRemaining(item.ticket);
  const atMax = item.quantity >= remaining;
  const atMin = item.quantity <= 1;

  return (
    <div className="bg-[#031c14] border border-green-900/30 rounded-xl p-4">
      <p className="text-[10px] tracking-widest text-green-400/60 uppercase mb-1">
        {item.eventName}
      </p>

      <p className="font-semibold text-sm">{item.ticket.name}</p>

      {item.ticket.tickets_per_order > 1 && (
        <p className="text-[10px] text-gray-500 mt-0.5">
          {item.ticket.tickets_per_order} entradas por unidad
        </p>
      )}

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1.5 bg-[#021610] rounded-full px-1 py-1 border border-green-900/40">
          <button
            onClick={() => onQtyChange(item.quantity - 1)}
            disabled={atMin}
            className={`w-6 h-6 rounded-full transition flex items-center justify-center text-sm leading-none
              ${atMin ? "text-green-900 cursor-not-allowed" : "text-green-400 hover:bg-green-900/50"}`}
          >
            −
          </button>
          <span className="text-sm w-5 text-center tabular-nums">
            {item.quantity}
          </span>
          <button
            onClick={() => onQtyChange(item.quantity + 1)}
            disabled={atMax}
            className={`w-6 h-6 rounded-full transition flex items-center justify-center text-sm leading-none
              ${atMax ? "text-green-900 cursor-not-allowed" : "text-green-400 hover:bg-green-900/50"}`}
          >
            +
          </button>
        </div>

        <div className="flex items-center gap-3">
          <p className="text-green-400 font-semibold text-sm">
            {formatCurrency(item.ticket.price * item.quantity, item.currency)}
          </p>
          <button
            onClick={onRemove}
            className="text-gray-600 hover:text-red-400 transition"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {atMax && (
        <p className="text-[10px] text-yellow-500/70 mt-2 tracking-wide">
          Máximo disponible alcanzado
        </p>
      )}
    </div>
  );
}
